import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import mainColors from '../../../themes/main-colors';
import { useTranslation } from '../../../hooks/useTranslation';
import { Link } from '@reach/router';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: mainColors.riverBlue,
    borderRadius: 4,
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: `inset 0px 2px 0px ${mainColors.sparkBlue}, 0px 3px 6px #00000029`,
    color: mainColors.noirBlur,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  iconButton: {
    padding: 4,
    color: mainColors.noirBlur,
  },
  innerContainer: {
    backgroundColor: mainColors.white,
    borderRadius: 4,
    padding: theme.spacing(2.5),
  },
  cardWrapper: {
    height: '100%',
  },
  promoCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    height: '100%',
  },
  textContent: {
    flex: 1,
    minWidth: 0,
  },
  titleBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: '20px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  description: {
    fontSize: '16px',
    lineHeight: 'normal',
    marginBottom: theme.spacing(2),
  },
  cta: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 600,
    color: mainColors.sparkBlue,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  image: {
    width: 80,
    height: 80,
    objectFit: 'cover',
    borderRadius: 3,
    flexShrink: 0,
  },
  rightSection: {
    paddingLeft: theme.spacing(2),
    borderLeft: `1px solid ${mainColors.lightGray1}`,
    borderTop: 'none',
  },
  leftSection: {
    paddingRight: theme.spacing(2),
  },

  [theme.breakpoints.down('sm')]: {
    leftSection: {
      paddingRight: 0,
      marginBottom: theme.spacing(2),
    },
    rightSection: {
      paddingLeft: 0,
      marginTop: theme.spacing(2),
      borderLeft: 'none',
      borderTop: `1px solid ${mainColors.lightGray1}`,
      paddingTop: theme.spacing(3),
    },
  },
}));

const EnergyJourneyCard = () => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const { t } = useTranslation();

  const handleToggle = () => {
    setExpanded(prev => !prev);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.headerTitle}>
          {t('YOUR_ENERGY_JOURNEY_STARTS_HERE')}
        </Typography>

        <IconButton
          className={classes.iconButton}
          onClick={handleToggle}
          aria-label={expanded ? 'Collapse section' : 'Expand section'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box className={classes.innerContainer}>
          <Grid container alignItems="stretch">
            {/* Left Card */}
            <Grid item xs={12} md={6} className={classes.leftSection}>
              <Box className={classes.cardWrapper}>
                <Box className={classes.promoCard}>
                  <Box className={classes.titleBlock}>
                    <Box>
                      <Typography className={classes.title}>
                        {t('ENERGY_JOURNEY_WELCOME_TO_PGE_TITLE')}
                      </Typography>
                      <Typography className={classes.description}>
                        {t('ENERGY_JOURNEY_WELCOME_TO_PGE_DESC')}
                      </Typography>
                    </Box>
                    <Box>
                      <img
                        src={t('ENERGY_JOURNEY_WELCOME_TO_PGE_IMAGE')}
                        alt={t('ENERGY_JOURNEY_WELCOME_TO_PGE_TITLE')}
                        className={classes.image}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Link
                      to={t('ENERGY_JOURNEY_WELCOME_TO_PGE_CTA_LINK')}
                      className={classes.cta}
                    >
                      {t('ENERGY_JOURNEY_WELCOME_TO_PGE_CTA')}
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Card */}
            <Grid item xs={12} md={6} className={classes.rightSection}>
              <Box className={classes.cardWrapper}>
                <Box className={classes.promoCard}>
                  <Box className={classes.titleBlock}>
                    <Box>
                      <Typography className={classes.title}>
                        {t('ENERGY_JOURNEY_PROGRAMS_TITLE')}
                      </Typography>
                      <Typography className={classes.description}>
                        {t('ENERGY_JOURNEY_PROGRAMS_DESC')}
                      </Typography>
                    </Box>
                    <Box>
                      <img
                        src={t('ENERGY_JOURNEY_PROGRAMS_IMAGE')}
                        alt="Welcome to PGE"
                        className={classes.image}
                      />
                    </Box>
                  </Box>
                  <Link
                    to={t('ENERGY_JOURNEY_PROGRAMS_CTA_LINK')}
                    className={classes.cta}
                  >
                    {t('ENERGY_JOURNEY_PROGRAMS_CTA')}
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

export default EnergyJourneyCard;
